/**
 * Hill chart with the live operating point per unit (§7.1, view 3).
 *
 * Efficiency contours over (q, h) from the site's own `efficiency(q, h)` — the same function the
 * engine solves with, so the point on screen is the point the machine is at.
 */

import { useEffect, useRef } from 'react';
import { efficiency } from '../../engine/hillchart';
import type { Snapshot } from '../../engine/snapshot';
import type { SiteConfig } from '../../engine/types';

export function HillChart({
  site,
  snap,
  selected,
  height = 220,
}: {
  site: SiteConfig;
  snap: Snapshot;
  selected: number;
  height?: number;
}): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const cssW = canvas.parentElement?.clientWidth ?? 400;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, height);

    const padL = 44;
    const padB = 26;
    const padT = 10;
    const padR = 10;
    const w = cssW - padL - padR;
    const h = height - padT - padB;

    const qMin = 0;
    const qMax = 1.15;
    const hMin = 0.7;
    const hMax = 1.15;
    const model = site.powerhouse.efficiency;

    // Efficiency field as a coarse raster — cheap, and it reads as a hill.
    const cell = 4;
    for (let px = 0; px < w; px += cell) {
      for (let py = 0; py < h; py += cell) {
        const q = qMin + ((px + cell / 2) / w) * (qMax - qMin);
        const hh = hMax - ((py + cell / 2) / h) * (hMax - hMin);
        const e = efficiency(model, q, hh);
        const t = Math.max(0, Math.min(1, (e - 0.6) / 0.35));
        // Deep water at low efficiency → light at the peak. One hue, brightness carries the value.
        const l = 12 + t * 46;
        ctx.fillStyle = `hsl(203 55% ${l}%)`;
        ctx.fillRect(padL + px, padT + py, cell, cell);
      }
    }

    // Contours at fixed efficiencies.
    ctx.font = '9px ui-sans-serif, system-ui, sans-serif';
    ctx.strokeStyle = 'rgba(223,228,232,0.35)';
    ctx.lineWidth = 1;
    for (const level of [0.7, 0.8, 0.88, 0.92]) {
      ctx.beginPath();
      let started = false;
      for (let px = 0; px <= w; px += 2) {
        const q = qMin + (px / w) * (qMax - qMin);
        // Solve h for this efficiency along each q column by scanning.
        let found: number | null = null;
        for (let s = 0; s <= 60; s++) {
          const hh = hMin + (s / 60) * (hMax - hMin);
          if (efficiency(model, q, hh) >= level) {
            found = hh;
            break;
          }
        }
        if (found === null) {
          started = false;
          continue;
        }
        const py = padT + ((hMax - found) / (hMax - hMin)) * h;
        if (!started) {
          ctx.moveTo(padL + px, py);
          started = true;
        } else ctx.lineTo(padL + px, py);
      }
      ctx.stroke();
    }

    // Axes.
    ctx.strokeStyle = '#2b3239';
    ctx.beginPath();
    ctx.moveTo(padL, padT);
    ctx.lineTo(padL, padT + h);
    ctx.lineTo(padL + w, padT + h);
    ctx.stroke();
    ctx.fillStyle = '#8b959d';
    ctx.textAlign = 'center';
    ctx.fillText('q = Q / Q_rated', padL + w / 2, height - 8);
    ctx.save();
    ctx.translate(12, padT + h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('h = H / H_rated', 0, 0);
    ctx.restore();
    ctx.textAlign = 'right';
    for (const v of [0.8, 0.9, 1.0, 1.1]) {
      const py = padT + ((hMax - v) / (hMax - hMin)) * h;
      ctx.fillText(v.toFixed(1), padL - 4, py + 3);
    }
    ctx.textAlign = 'center';
    for (const v of [0, 0.25, 0.5, 0.75, 1.0]) {
      const px = padL + ((v - qMin) / (qMax - qMin)) * w;
      ctx.fillText(v.toFixed(2), px, padT + h + 12);
    }

    // Operating points.
    snap.units.forEach((u, i) => {
      if (u.mode !== 'online' || u.q <= 0) return;
      const q = u.q / site.powerhouse.ratedFlow;
      const hh = u.netHead / site.powerhouse.ratedHeadM;
      const px = padL + ((q - qMin) / (qMax - qMin)) * w;
      const py = padT + ((hMax - hh) / (hMax - hMin)) * h;
      const isSel = i === selected;
      ctx.beginPath();
      ctx.arc(px, py, isSel ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = isSel ? '#dfe4e8' : 'rgba(223,228,232,0.55)';
      ctx.fill();
      ctx.fillStyle = '#0e1113';
      ctx.font = 'bold 9px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(i + 1), px, py + 0.5);
      ctx.textBaseline = 'alphabetic';
      ctx.font = '9px ui-sans-serif, system-ui, sans-serif';
    });
  });

  return (
    <div style={{ width: '100%', height }}>
      <canvas ref={ref} role="img" aria-label="Turbine hill chart with live operating points" />
    </div>
  );
}
